import type { FandomCmp } from "common-types";
import type { AdEngine } from "../../../../core/AdEngine";
import { AdEnginePlugin, type Hooks } from "../../../../core/AdEnginePlugin";
import type { AdNode } from "../../../../core/AdNode";
import type { AdNodeManager } from "../../../../core/AdNodeManager";
import type { GptWrapper } from "../../../../core/GptWrapper";
import {
  DEFAULT_TARGETING_VALUE,
  type DoubleVerifyPluginConfig,
  type DoubleVerifyResponse,
  type DoubleVerifySlotResponseMap,
  type DoubleVerifySlotTargeting,
  type PageTargeting,
} from "./types";
import { isConsentGranted } from "../../../../helpers/isConsentGranted";

/** The endpoint base URL provided by DoubleVerify */
const signalsUrl = "https://pub.doubleverify.com/signals/pub.json";
/** Provided by DoubleVerify */
const ctx = "28150781";
/** Provided by DoubleVerify */
const cmp = "DV1001654";
/** The current page url to use in the `url` query param and the `referer` header value */
const referer = window.location.href;

/** The default values to set on the page targeting before the DoubleVerify request resolves */
const DefaultPageTargeting: PageTargeting = {
  abs: DEFAULT_TARGETING_VALUE,
  bsc: DEFAULT_TARGETING_VALUE,
  ids: DEFAULT_TARGETING_VALUE,
};

/**
 * The list of base slot names to track for all sites.
 *
 * TODO: ideally this will be part of a DoubleVerify plugin config post launch when we start the
 * process of moving plugin configurations out of the code
 */
const slotNamesToTrack: string[] = [
  "top_leaderboard", // ucp, f2, comicvine, gamefaqs, gamespot, giantbomb, metacritic, metacritic-neutron, tvguide
  "top_boxad", // ucp, f2
  "incontent_boxad", // ucp, f2
  "bottom_leaderboard", // ucp, f2
  "incontent_leaderboard", // ucp
  "gallery_leaderboard", // ucp
  "featured", // ucp, f2
  "incontent_player", // ucp
  "mpu_top", // comicvine, gamefaqs, gamespot, giantbomb
  "mpu-plus-top", // metacritic, metacritic-neutron, tvguide
  "cdm-zone-01", // muthead
  "cdm-zone-02", // muthead
  "cdm-zone-03", // muthead
  "cdm-zone-04", // muthead
  "cdm-zone-06", // muthead
];

export class DoubleVerifyPlugin extends AdEnginePlugin {
  private adNodeManager?: AdNodeManager;
  private gptWrapper?: GptWrapper;
  private consent = false;
  private initialized = false;
  private trackedSlots: Record<string, AdNode> = {};

  /**
   * Checks if the plugin should be enabled on this page using the InstantConfig `icDoubleVerify`
   * property
   * @param instantConfig The instance config objet with the `icDoubleVerify` property to see if the
   * plugin is enabled on the page
   * @returns True if the plugin is enabled, false otherwise
   */
  static isEnabled({ icDoubleVerify }: DoubleVerifyPluginConfig): boolean {
    return !!icDoubleVerify;
  }

  /**
   * Builds the URL for the request to DoubleVerify. It takes the base URL from DoubleVerify and
   * adds in all the expected query parameters for:
   * - `ctx` and `cmp` values provided by DoubleVerify
   * - `url` of the current page
   * - A query param for each ad to track on the page in the format of `adunits[${adUnitPath}][]=`
   *   (there is no value after the `=`)
   * @returns The URL object for this request
   */
  private buildRequestUrl(): URL {
    const url = new URL(signalsUrl);
    const searchParams = url.searchParams;

    // Add the ctx and cmp values
    searchParams.set("ctx", ctx);
    searchParams.set("cmp", cmp);
    searchParams.set("url", referer);

    for (const adUnitPath of Object.keys(this.trackedSlots)) {
      searchParams.append(`adunits[${adUnitPath}][]`, "");
    }

    return url;
  }

  /**
   * Builds the request to the DoubleVerify endpoint for the current page and the tracked slots on
   * the page. When the request is completed it will handle the response and set the targeting
   * values.
   *
   * @returns Promise that resolves when the request is complete and the targeting has been set or
   * a console error was emitted.
   */
  private async fetchData() {
    if (!this.gptWrapper || !this.adNodeManager || !this.consent) {
      return;
    }

    const url = this.buildRequestUrl();

    try {
      const response = await fetch(url, { headers: { referer } });
      if (response.status >= 400) {
        // handle error
        return;
      }

      const responseJson = await response.json();
      this.handleResponse(responseJson);
    } catch (error: unknown) {
      console.error("Failed to request data from DoubleVerify", error);
    }
  }

  /**
   * Takes the DoubleVerify response object and sets all the targeting values on the PageTargeting
   * and the SlotTargeting on each slot in the response.
   *
   * @param response The full object from DoubleVerify response
   */
  private handleResponse(response: DoubleVerifyResponse) {
    const { ABS, BSC, IDS, TVP, VLP } = response;
    if (this.gptWrapper) {
      this.gptWrapper.setTargeting("abs", ABS);
      this.gptWrapper.setTargeting("bsc", BSC);
      this.gptWrapper.setTargeting("ids", IDS.toString());
    }

    this.setTargetingForSlots("tvp", TVP);
    this.setTargetingForSlots("vlp", VLP);
  }

  /**
   * Sets the provided targeting key on the slot to the value within the slot reponse object.
   * Since tvp and vlp are the same object format, we can use the same function to handle both
   * keyvalues in the response.
   *
   * @param key The key to set targeting for, either tvp or vlp
   * @param slotResponseMap The response map with the specific values for the ad unit path of this
   * slot. This object is basically just { "": <value> }. The `""` is a bit weird, but see
   * {@link DoubleVerifySlotResponseMap} for more information on why. This defaults to `{}` since
   * if DoubleVerify gets a request with no ad unit paths or unknown ad unit paths, it will respond
   * without these objects.
   */
  private setTargetingForSlots(
    key: keyof DoubleVerifySlotTargeting,
    slotResponseMap: DoubleVerifySlotResponseMap = {},
  ) {
    for (const [adUnitPath, value] of Object.entries(slotResponseMap)) {
      const adNode = this.trackedSlots[adUnitPath];
      adNode?.setTargeting(key, value[""]);
    }
  }

  /**
   * Initializes the plugin by setting default targeting values and then finding all the current
   * ad slots on the page that are marked to be tracked by DoubleVerify and building out the mapping
   * of AdUnitPath to AdNode on `this.trackedSlots`. This mapping will be used to request the
   * slot keyvalues from Double Verify and then add the keyvalues when the response returns.
   */
  // eslint-disable-next-line complexity
  private initialize() {
    if (
      !this.consent ||
      !this.gptWrapper ||
      !this.adNodeManager ||
      this.initialized
    ) {
      return;
    }

    // Set initial page targeting values
    for (const [key, value] of Object.entries(DefaultPageTargeting)) {
      this.gptWrapper.setTargeting(key, value);
    }

    // get the slots that are on the page and send the request
    const adNodesOnPage = this.adNodeManager.getAdNodes();
    for (const adNode of adNodesOnPage) {
      // Check if the baseSlotName or slotName matches a slot name expected to be tracked by DV
      if (
        !slotNamesToTrack.includes(adNode.getBaseSlotName()) &&
        !slotNamesToTrack.includes(adNode.getSlotName())
      ) {
        // Not a slot we need to verify, continue with next slot
        continue;
      }

      // Set initial slot targeting values
      adNode.setTargeting("vlp", DEFAULT_TARGETING_VALUE);
      adNode.setTargeting("tvp", DEFAULT_TARGETING_VALUE);

      // Store the slot, indexed by adUnitPath, for quick retrieval of the ad node later when the
      // double verify response is handled.
      const adUnitPath = adNode.getAdUnitPath();
      if (adUnitPath) {
        this.trackedSlots[adUnitPath] = adNode;
      } else {
        console.warn(
          `[DoubleVerifyPlugin] AdUnitPath for ${adNode.getSlotName()} not defined. Skipping slot`,
        );
      }
    }

    this.initialized = true;

    // We are initialized, so kick off the fetch
    this.fetchData();
  }

  hooks: Hooks = {
    /**
     * Hook triggered when consent is updated.
     * @param fandomCmp - The consent parameters.
     */
    onConsentUpdated: (fandomCmp: FandomCmp) => {
      if (!isConsentGranted(fandomCmp)) {
        return;
      }

      this.consent = true;

      this.initialize();
    },
    onAdEngineInit: (adEngine: AdEngine) => {
      this.gptWrapper = adEngine.gptWrapper;
      this.adNodeManager = adEngine.adNodeManager;

      this.initialize();
    },
  };
}
